/**
 * Resource Management help narrations (Clara + Liam).
 */

export const RESOURCES_NARRATIONS = {
  'portfolio-evm-help': [
    {
      title: 'One job is not the whole story',
      icon: '🎯',
      turns: [
        {
          speaker: 'clara',
          speech: 'The job E V M page tells you how one job is doing. This page asks the same question about a group of jobs. A program, a portfolio, a whole division, or any mix you tick at the top. Nothing is estimated again here. Every figure is the job\u2019s own E V M, added up.',
        },
        {
          speaker: 'liam',
          speech: 'Four pickers. Portfolios and programs are the ones saved on the Corporation Resource Histogram. A program is a set of jobs, a portfolio is a set of programs. Divisions brings in every live job a company owns. Jobs lets you add a single one. Tick whatever you like and press Apply. If the same job arrives twice, say from a program and from its division, it is counted once. The page remembers your last scope for your login.',
        },
      ],
    },
    {
      title: 'What gets added and what gets recomputed',
      icon: '➕',
      turns: [
        {
          speaker: 'clara',
          speech: 'Budget at completion, planned value, earned, spent, and remaining hours are summed across the jobs. The estimate at completion is the sum of each job\u2019s own estimate at completion. So a job running at a C P I of zero point six keeps its own overrun instead of being priced at the group average.',
        },
        {
          speaker: 'liam',
          speech: 'And the indices, S P I, C P I, and T C P I, are recomputed from those sums. Never an average of the job indices. Two jobs at one point one and zero point five do not make zero point eight. The data date is the latest job\u2019s P six data date. If the jobs differ, a yellow note says so and the table shows each job\u2019s own date.',
        },
      ],
    },
    {
      title: 'Reading the chart and the table',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'Bars are weekly hours stacked by job. The Bars dropdown switches them between budget, earned, spent, and remaining. The lines are cumulative for the whole scope: budget, earned, spent, and the dashed forecast, which is earned so far plus remaining. The dotted vertical line is the closed week. Spent and earned stop there, remaining starts the week after. Click a job in the legend to hide it.',
        },
        {
          speaker: 'liam',
          speech: 'The table under the chart is where you go when a group index looks wrong. One row per job with its own figures, and a column that says which program, portfolio, or division brought it in. Notes flag things worth opening the job for: earned above budget, nothing spent yet, an index out of range, or a data date more than four weeks behind the others. Flags never change the sums. Jobs with no P six budget, no earned, and no Maestro spent are greyed out and not counted. A job whose P six read failed is listed as unavailable and left out, and the Jobs card says how many.',
        },
      ],
    },
    {
      title: 'Apply versus Refresh',
      icon: '🔄',
      turns: [
        {
          speaker: 'clara',
          speech: 'Apply uses each job\u2019s cached P six read when it has one, so it comes back in a few seconds. Refresh re-reads P six for every job in the scope. The first read of a job takes a few seconds each, so a large division can take about a minute.',
        },
        {
          speaker: 'liam',
          speech: 'Include unawarded estimates adds estimates that have a P six proposal but no job yet. They bring budget and nothing spent, so leave it off unless you want to see committed work. Who sees the page: the same people as the Corporation Resource Histogram, Resources, Admin, Construction, Estimating, Operations, and Accounting, plus managers and above.',
        },
      ],
    },
  ],
  'fab-histogram-help': [
    {
      title: 'Who do we need, and when?',
      icon: '🏭',
      turns: [
        {
          speaker: 'clara',
          speech: 'This is the Corporation Resource Histogram. One question: how many people does the whole company need each week? Each coloured block is leftover hours on a job — field crews and the fab shops, East, West, F L, and Tank, all in one stack.',
        },
        {
          speaker: 'liam',
          speech: 'The dark line is how much the fab shops can take that week. Crew, hours per week, and absences all sit under that line. Filter Resource discipline to Fabrication and the stack goes over the line only when the shop really is full. Clear it and you are looking at the whole company — turn the line off then.',
        },
      ],
    },
    {
      title: 'When the hours move',
      icon: '📂',
      turns: [
        {
          speaker: 'clara',
          speech: 'The page opens on Primavera P six — every remaining resource on the working copies, not only shop fab. Every working copy counts, the file named Current and any numbered Current copies a job carries. Baselines stay in P six, primary and secondary, but the portal never reads one as a project, same as the P six project list. In the Project slicer each copy shows its full name: the project I D, the P six project name, the project codes, and P six\u2019s own data date, so you can tell two numbered copies apart and untick the one you do not want. Remaining is spread the way P six spreads it: units per hour times the calendar work hours each day, holidays empty, then rolled up by Sunday week. So a six by ten crew peaks where P six peaks. Excel data dumps are still there if you need to edit shop hours by hand. Portal cost breakdowns are not live yet.',
        },
        {
          speaker: 'liam',
          speech: 'For Excel, use Export for one file, adjust the hours, then Import that same file. Import also reloads the chart — there is no separate reload button. A one-file import wipes every Excel dump and splits them back by file name. A single dump with the same file name still replaces just that job. On P six, Refresh pulls the latest hours. The three sources do not mix on the chart.',
        },
      ],
    },
    {
      title: 'Crew and the shop week',
      icon: '⚙️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Open Planning when the crew or the shop week changes. Type men per shop, hours per week, and a company absence percent. It starts at eight. East and West can have their own calendar if they run different weeks.',
        },
        {
          speaker: 'liam',
          speech: 'The coloured bars stay as planned hours. And here is the part that used to confuse people: on the P six source, the men are P six’s men and nothing else. Hours are spread on the calendar the activity runs on, and a bar turns into people by dividing by that calendar’s own hours per week. A five by ten job divides by fifty hours, a six by ten by sixty — the same thing the Resource Usage Profile shows when it is based on hours per time period. Planning calendars only turn Excel dump hours into men, because those rows carry no calendar. Absenteeism stays out of the P six chart entirely. It does not change the bars, and it does not pull the capacity line down. On an Excel dump it still lowers that line only. No summarize needed in P six either — the pull reads the live remaining. Save planning when you are done.',
        },
      ],
    },
    {
      title: 'Keep the page quiet',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'The title stays on the left. Filters, KPIs, and Planning start open. Help, Audit Log, Import, Export, and PDF sit on the right. The page opens in man-weeks. Units also has man-months — that is man-weeks divided by the weeks in the month, the same average crew as the job Resource Histogram. Filters has Legend, same idea as that job page — colour by file name, project I D, or resource, and you can split by a second key so one slice is project and resource together. Below the portal keys you will find every P six field the selected copies carry: activity codes, U D Fs, and resource codes like Resource Standardization — the same list the E V M report legend uses. Add slicer offers those same columns, so a resource code can filter the bars, not only colour them. The page remembers the program, the legend, and the slicers for your login, so a refresh comes back to the same place. The S curve on the right is always hours, even when the bars are man weeks or man months. Window starts from today going forward — switch it to All weeks if you need history, or Through last week for the past only. Dates under the bars are three layers, same as P six: the Sunday day number, then the month, then the year. Bar totals on top of each stack are on by default — whole numbers, no tenths. Planning stays as wide as the histogram, not under the filter drawer. A program or a portfolio shrinks project and every other list to what still has hours.',
        },
        {
          speaker: 'liam',
          speech: 'Audit Log lists who imported a file, who saved planning, and who refreshed P six. A superadmin can revert planning or colour changes. Shop capacity twenty nine point five shows as thirty — same rounding as the jobs. Upload the same file name again and it replaces the old hours. It does not stack a second copy.',
        },
      ],
    },
    {
      title: 'Many jobs, one curve',
      icon: '📁',
      turns: [
        {
          speaker: 'clara',
          speech: 'This page already stacks every job on one curve. The legend still shows the P six name. Hover it and the balloon has the job and the estimate. Resource discipline starts on Fabrication so the shop conversation stays clean. Clear that slicer and every trade shows — that is the corporation view.',
        },
        {
          speaker: 'liam',
          speech: 'There are two layers above Project. A program is a set of jobs — Goodyear. A portfolio is a set of programs — Canada. Define them from P six X E Rs, not the Excel dumps. One checkbox then pulls the whole stack. P six is the opening source, so field hours are already there. Switch to Excel dumps when you only want the shop files. The dark capacity line is still shop crew, so turn it off when you are looking at the whole company.',
        },
      ],
    },
    {
      title: 'What comes next',
      icon: '✅',
      turns: [
        {
          speaker: 'clara',
          speech: 'Later, Estimation cost breakdowns in the portal will feed this same chart, so a bid change updates the load without a side spreadsheet. That switch is already on the page. It is not live yet.',
        },
        {
          speaker: 'liam',
          speech: 'Until then, this is the shop conversation. What is loaded, what is forecast, and where we are over the line. Every Monday at one in the afternoon we also email the current shop load. Combined peak includes estimates. Over capacity is awarded jobs only. Reply to Project Controls with men, start and finish, the estimate number, and the job number if it is awarded — or say what changed on something already on the file.',
        },
      ],
    },
  ],
};
